import { inject } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import Activation from '@/components/pages/activate/activation/activation-tmpl'
import ProjectTemplate from '@/components/pages/project-template/project-template-tmpl'
import ProjectAudio from '@/components/pages/project-audio/project-audio-tmpl'
import Create from '@/components/pages/create/create-tmpl'
import CurrentUser from '@/globals/current-user-info.js'
import Utils from '@/globals/utils.js'
import EventBus from '@/components/mixins/event-bus-mixin'
import Admin from '@/components/pages/admin/admin-tmpl'
import Home from '@/components/pages/home/home-tmpl'
import Menu from '@/components/pages/menu/menu-tmpl'
import Monitoring from '@/components/pages/monitoring/monitoring-tmpl'
import PageNotFound from '@/components/pages/page-not-found/page-not-found-tmpl'
import Player from '@/components/pages/player/player-tmpl'
import VirtualPlayerIframe from '@/components/pages/virtual-player-iframe/virtual-player-iframe-tmpl'
import Plan from '@/components/pages/plan/plans-tmpl'
import PurchasePlan from '@/components/pages/purchase-plan/purchase-plan-tmpl'
import Reporting from '@/components/pages/reporting/reporting-tmpl'
import Schedule from '@/components/pages/schedule/schedule-tmpl'
import ScheduleV2 from '@/components/pages/schedule-v2/schedule.vue'
// import Setup from '@/components/pages/setup/setup-tmpl'
import SignIn from '@/components/pages/sign-in/sign-in-tmpl'
import SignInSuccessful from '@/components/pages/sign-in-successful/sign-in-successful-tmpl'
import SmartText from '@/components/pages/smart-text/smart-text-tmpl'
import SampleDefaultLayout from '@/components/pages/structure-default-layout/default-layout-structure-tmpl'
import SampleAsideLayout from '@/components/pages/structure-aside-layout/aside-layout-structure-tmpl'
import TermsOfService from '@/components/pages/terms-of-service/terms-of-service-tmpl'
import UserSettings from '@/components/pages/user-settings/user-settings-tmpl'
import UserProfile from '@/components/pages/user-profile/user-profile-tmpl'
import GeneralTermsAndConditionsOfSale from '@/components/pages/general-terms-and-conditions-of-sale/general-terms-and-conditions-of-sale-tmpl'
import CookieList from '@/components/pages/cookie-list/cookie-list-tmpl'
import Privacy from '@/components/pages/privacy/privacy-tmpl'
import LicenseOrdering from '@/components/pages/activate/license-ordering/license-ordering-tmpl'
import Legal from '@/components/pages/legal/legal-tmpl'
import SSO from '@/components/pages/sso/sso-tmpl'
import LogoutOk from '@/components/pages/logout-ok/logout-ok-tmpl'
import AccountSwitched from '@/components/pages/account-switched/account-switched-tmpl'
import LeftAccount from '@/components/pages/left-account/left-account-tmpl'
import NoProductAccess from '@/components/pages/no-product-access/no-product-access-tmpl'
import MediaWrapper from '@/components/pages/create/media/index.vue'

import CONST from '@/globals/constants.js'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';

import { t } from '@/i18n/i18ns'

const ARYA_SUSPENDED_EXCLUDED_PATHS = ['/settings', '/settings/account', '/settings/customer-info', '/activation', '/user-admin', '/distributor']
const ARYA_SUSPENDED_EXCLUDED_NAMES = ['signIn', 'signInSuccessful', 'account-switched', 'activation', 'user-admin', 'distributor']

const NO_PRODUCT_ACCESS_EXCLUDED_PATHS = ['/no-product-access', '/user-profile', '/act-user-profile']
const NO_PRODUCT_ACCESS_EXCLUDED_NAMES = ['noProductAccess', 'userProfile', 'activateUserProfile']

const RouterConfig = createRouter({
  history: createWebHistory(),
  routes: [{
    path: '/',
    name: 'signIn',
    component: SignIn,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE}` }
  },
  {
    path: '/home',
    name: 'home',
    component: Home,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Home` }
  },
  {
    path: '/loginok',
    name: 'signInSuccessful',
    component: SignInSuccessful,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE}` }
  },
  {
    path: '/media',
    name: 'media',
    component: MediaWrapper,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Files` },
    props: { mediaComponentAs: CONST.MEDIA_COMPONENT_AS_MEDIA_FILES }
  },
  {
    path: '/create/:pageContent?',
    name: 'create-template-set',
    component: Create,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Create` }
  },
  {
    path: '/alert',
    name: 'alert',
    component: MediaWrapper,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Alert` },
    props: { mediaComponentAs: CONST.MEDIA_COMPONENT_AS_ALERT_FILES }
  },
  {
    path: '/menu',
    name: 'menuComp',
    component: Menu,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Menu` }
  },
  {
    path: '/schedule',
    name: 'schedules',
    component: Schedule,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Schedule` }
  },
  {
    path: '/schedule-v2',
    name: 'schedule-v2',
    component: ScheduleV2,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Schedule V2` }
  },
  {
    path: '/monitoring/:monitoringOptions?',
    name: 'monitoring',
    component: Monitoring,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - monitoring` }
  },
  {
    path: '/setup/:serialNumber?/:playerName(.*)?',
    name: 'setup',
    component: Player,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Setup` }
  },
  {
    path: '/smart-text/:smartTextId(.*)?',
    name: 'smartText',
    component: SmartText,
    props: { routeNameProp: 'smart-text-default' },
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Smart Text` }
  },
  {
    path: '/player/play/:serialNumber?',
    name: 'virtual-player-iframe',
    component: VirtualPlayerIframe,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Player Play` }
  },
  {
    path: '/player/:serialNumber?/:playerName(.*)?',
    name: 'player',
    component: Player,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Players` }
  },
  {
    path: '/plans',
    name: 'plans',
    component: Plan,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Plans` }
  },
  {
    path: '/purchase/:plan?/:centerPanel?',
    name: 'purchase',
    component: PurchasePlan,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Purchase` }
  },
  {
    path: '/template/:setId/:templateId?',
    name: 'create-project-template',
    component: ProjectTemplate,
    props: { routeNameProp: 'create-project-template' },
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Create` }
  },
  {
    path: '/project-edit/:projectId/:projectFrameAction?',
    name: 'edit-project-template',
    component: ProjectTemplate,
    props: { routeNameProp: 'edit-project-template' },
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Create` }
  },
  {
    path: '/project-audio/:projectId/:projectFrameAction?',
    name: 'edit-project-audio',
    component: ProjectAudio,
    props: { routeNameProp: 'edit-project-audio' },
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Create` }
  },
  {
    path: '/led',
    name: 'led-configuration',
    component: MediaWrapper,
    props: { mediaComponentAs: CONST.MEDIA_COMPONENT_AS_LED_FILES },
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - LED Config` }
  },
  {
    path: '/videowall',
    name: 'videowall',
    component: MediaWrapper,
    props: { mediaComponentAs: CONST.MEDIA_COMPONENT_AS_VIDEOWALL_FILES },
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Video Wall` }
  },
  {
    path: '/aside-layout',
    name: 'aside-layout',
    component: SampleAsideLayout,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Aside Layout` }
  },
  {
    path: '/account-switched',
    name: 'account-switched',
    component: AccountSwitched,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Account Switched` }
  },
  {
    path: '/left-account/:accountName?',
    name: 'left-account',
    component: LeftAccount,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Left Account` }
  },
  {
    path: '/default-layout',
    name: 'default-layout',
    component: SampleDefaultLayout,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Default Layout` }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'page-not-found',
    component: PageNotFound,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Page Not Found` }
  },
  {
    path: '/reporting',
    name: 'reporting',
    component: Reporting,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Reporting` }
  },
  {
    path: '/tos',
    name: 'tos',
    component: TermsOfService,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Tems of Service` }
  },
  {
    path: '/user-profile',
    name: 'userProfile',
    component: UserProfile,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - User Profile` }
  },
  {
    path: '/act-user-profile',
    name: 'activateUserProfile',
    component: UserProfile,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - User Profile` }
  },
  {
    path: '/settings/:centerPanel?/:id?',
    name: 'settings',
    component: UserSettings,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Settings` }
  },
  {
    path: '/user-admin',
    name: 'user-admin',
    component: Admin,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Admin` }
  },
  {
    path: '/general-terms-and-conditions-of-sale-12092019',
    name: 'generalTermsAndConditionsOfSale',
    component: GeneralTermsAndConditionsOfSale,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - GeneralTermsAndConditionsOfSale` }
  },
  {
    path: '/cookie-notice',
    name: 'cookieList',
    component: CookieList,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Cookie Policy` }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: Privacy,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Privacy` }
  },
  {
    path: '/distributor',
    name: 'distributor',
    component: LicenseOrdering,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Distributor` }
  },
  {
    path: '/activation',
    name: 'activation',
    component: Activation,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Activation New` }
  },
  {
    path: '/legal',
    name: 'legal',
    component: Legal,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Legal` }
  },
  {
    path: '/sso/:providerName?',
    name: 'ssoStartPage',
    component: SSO,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - SSO` }
  },
  {
    path: '/logoutok/:providerName?',
    name: 'ssoLogoutokPage',
    component: LogoutOk,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - Logout` }
  },
  {
    path: '/no-product-access',
    name: 'noProductAccess',
    component: NoProductAccess,
    meta: { title: `${CONFIG.STAGE_PAGE_TITLE} - No Product Access` }
  }]
})

// Account suspension redirection handler
RouterConfig.aryaSuspendedRedirection = async function () {
  // No redirection if currentRoute is excluded
  if (ARYA_SUSPENDED_EXCLUDED_PATHS.includes(RouterConfig.currentRoute.value.path)) {
    return
  }

  try {
    await RouterConfig.push('/settings/account');
  } catch (error) {
    // Suppress thrown errors for duplicated and cancelled navigation
    return error?.name;
  }
}

// Account suspension redirection handler
RouterConfig.noProductAccessRedirection = async function () {
  // No redirection if currentRoute is excluded
  if (NO_PRODUCT_ACCESS_EXCLUDED_PATHS.includes(RouterConfig.currentRoute.value.path)) {
    return
  }

  try {
    await RouterConfig.push('/no-product-access');
  } catch (error) {
    // Suppress thrown errors for duplicated and cancelled navigation
    return error?.name;
  }
}

RouterConfig.afterEach((to, from, next) => {
  if (window.ga) {
    ga('set', 'page', to.path)
    ga('send', 'pageview')
  }
})

RouterConfig.beforeEach(async (to, from, next) => {
  await useCognitoAuthStore().fetchAndStoreAuthSession()
  // Set Browser's tab title based on the current page
  if ((to && to.meta) && to.meta.title) {
    if (Utils.isPageForLicenses()) {
      document.title = to.meta.title.replace('ARYA', 'DSOS')
    } else {
      document.title = to.meta.title
    }
  }

  // If user is not logged in and directly access the setup page in the url, save it to localstorage and will be targeted upon loggedin
  if ((to.hash === '#/setup') && !useCognitoAuthStore().isAuthenticated) {
    localStorage.setItem('targetPageAfterLogin', 'setup')
  }

  const $signedInUserInfo = inject('$signedInUserInfo')
  if (useCognitoAuthStore().isAuthenticated) {
    if (!(CurrentUser.hasAccessToSelectedProduct())) {
      // Perform check for no product access and redirect to no product access page
      // Bypassed path names and routes
      if (!NO_PRODUCT_ACCESS_EXCLUDED_PATHS.includes(to.path) && !NO_PRODUCT_ACCESS_EXCLUDED_NAMES.includes(to.name)) {
        next('/no-product-access')
        return
      }
    } else if ($signedInUserInfo?.value?.activePlan?.suspended) {
      // Perform check for suspended accounts and redirect to settings
      // Bypassed path names and routes
      if (!ARYA_SUSPENDED_EXCLUDED_PATHS.includes(to.path) && !ARYA_SUSPENDED_EXCLUDED_NAMES.includes(to.name)) {
        next('/settings/account')
        return
      }
    }
  }

  // Page Access rights logic based on User's Rights
  const transitionToPageName = to.name
  const transitionToPageParams = to.params

  const pageTransitionContract = {
    setup: () => {
      if (!CurrentUser.hasUserRightPlayer()) {
        pageTransitionContract.showWarningDialog()
        return false
      } else if (to.params.serialNumber && to.params.playerName) {
        next(`/player/${to.params.serialNumber}/${to.params.playerName}`)
        return false
      } else if (to.params.serialNumber) {
        next(`/player/${to.params.serialNumber}/`)
        return false
      }

      next('/player')
      return false
    },
    menuComp: () => {
      // When user has ONLY billing right, redirect to profile automatically
      if (CurrentUser.hasGroupExtended() && !CurrentUser.hasGroupUserAdmin()) {
        const hasNoRightsAsideFromBilling = [CurrentUser.hasGroupUserCreate(), CurrentUser.hasGroupUserCreateLock(), CurrentUser.hasGroupUserAlert(), CurrentUser.hasGroupUserPlayer(), CurrentUser.hasGroupUserSchedule()].every((hasRights) => { return !hasRights })

        if (CurrentUser.hasGroupUserBilling() && hasNoRightsAsideFromBilling) {
          next('/user-profile')
          return false
        }
        return true
      }

      return true
    },
    media: () => {
      /**
       * Media Files Page should not be accessible to
       * - User with no modify rights
       * - user with no schedule rights
       */
      if (!CurrentUser.hasUserRightModify() && !CurrentUser.hasUserRightSchedule()) {
        next('/menu')
        return false
      }

      return true
    },
    'create-template-set': () => {
      /**
       * Parent Create page accessibility contract
       * Create Page should be accessible to
       * - User with no creates rigths or lock_create rights
       * - user with schedule rights
       */
      if (!CurrentUser.hasUserRightModify() && !CurrentUser.hasUserRightSchedule()) {
        next('/menu')
        return false
      }

      /**
       * Create Custom template sub page accessibility contract
       * Create custom template should be accissble to
       * - user with create rights
       * - admin rights
       */
      if (transitionToPageParams.pageContent === 'new') {
        if (!CurrentUser.hasUserRightCreate()) {
          next('/menu')
          return false
        }

        if (CurrentUser.hasGroupUserLimited()) {
          next('/menu')
          return false
        }
        return true
      }

      /**
       * Template lists sub page accessibility contract
       * Template lists is acessible to user with modify rights
       * - free users (not extended)
       * - admin
       * - user with create_lock group
       * - user with create group
       */
      if (transitionToPageParams.pageContent === undefined || transitionToPageParams.pageContent === 'template') {
        if (!CurrentUser.hasUserRightModify()) {
          next('/media')
          return false
        }

        if (CurrentUser.hasGroupUserLimited()) {
          next('/media')
          return false
        }

        return true
      }

      /**
       * Audio
       * Audio is acessible to extended user only
       * - admin
       * - user with create_lock group
       */
      if (transitionToPageParams.pageContent === 'audio') {
        if (!CurrentUser.hasGroupExtended()) {
          next('/menu')
          return false
        }

        if (!CurrentUser.hasUserRightModify()) {
          next('/menu')
          return false
        }

        if (CurrentUser.hasGroupUserLimited()) {
          next('/menu')
          return false
        }
      }
      return true
    },
    alert: () => {
      if (!CurrentUser.hasUserRightAlert()) {
        next('/menu')
        return false
      }
      return true
    },
    'create-project-template': () => {
      if (!CurrentUser.hasUserRightModify()) {
        pageTransitionContract.showWarningDialog()
        return false
      }
      // Should not be accessable because custom has it's own route
      if (transitionToPageParams.templateId && transitionToPageParams.templateId === 'custom') {
        next('/create/new')
        return false
      }
      // Should not be accessable because custom has it's own route
      if (transitionToPageParams.templateId && transitionToPageParams.templateId === 'custom-0001') {
        next('/create/new')
        return false
      }
      // Should not be accessable because alert has it's own route
      if (transitionToPageParams.templateId && transitionToPageParams.templateId === 'alert-0001') {
        next('/alert')
        return false
      }

      return true
    },
    'edit-project-template': () => {
      if (!CurrentUser.hasUserRightModify() && !CurrentUser.hasUserRightAlert()) {
        pageTransitionContract.showWarningDialog()
        return false
      }

      return true
    },
    'user-admin': () => {
      // Premium
      if (
        CurrentUser.isStandAloneUser() ||
        CurrentUser.isFreeUser() ||
        !CurrentUser.hasUserRightAdmin()
      ) {
        next('/menu')
        return false
      }

      return true
    },
    plans: () => {
      if (Utils.isSignUpDisallowed() || CurrentUser.hasGroupEnterprise()) {
        next('/menu')
        return false
      }

      return true
    },
    purchase: () => {
      return true
    },
    userProfile: () => {
      if (Utils.isPageForLicenses()) {
        return false
      }

      return true
    },
    activateUserProfile: () => {
      if (!Utils.isPageForLicenses()) {
        return false
      }

      return true
    },
    tutorials: () => {
      // When user has ONLY billing right, redirect to profile automatically
      if (CurrentUser.hasGroupExtended() && !CurrentUser.hasGroupUserAdmin()) {
        const otherGroupIsNotPresent = [CurrentUser.hasGroupUserCreate(), CurrentUser.hasGroupUserCreateLock(), CurrentUser.hasGroupUserAlert(), CurrentUser.hasGroupUserPlayer(), CurrentUser.hasGroupUserSchedule()].every((hasGroup) => { return !hasGroup })

        if (CurrentUser.hasGroupUserBilling() && otherGroupIsNotPresent) {
          next('/user-profile')
          return false
        }
        return true
      }

      return true
    },
    player: () => {
      if (!CurrentUser.hasUserRightPlayer()) {
        next('/menu')
        return false
      }
      return true
    },
    reporting: () => {
      // Only Extended
      if (!CurrentUser.hasUserRightReporting()) {
        next('/menu')
        return false
      }

      return true
    },
    monitoring: () => {
      if (!CurrentUser.hasUserRightMonitoring()) {
        next('/menu')
        return false
      }
      return true
    },
    schedules: () => {
      if (!CurrentUser.hasUserRightSchedule()) {
        next('/menu')
        return false
      }

      return true
    },
    distributor: () => {
      // Show pop-up as if page not found if activate domain is not present from config
      if (!CONFIG.FRONTEND_ACTIVATE_DOMAIN) {
        setTimeout(() => {
          EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
            type: 'error',
            alert: {
              message: t('label.pageNotFound')
            },
            callbacks: {
              eventOnClose: () => {
                next('/menu')
              }
            }
          })
        }, 1)

        return false
      }

      if (!CurrentUser.hasGroupDistributor()) {
        next('/activation')
        return false
      }

      return true
    },
    activation: () => {
      // Show pop-up as if page not found if activate domain is not present from config
      if (!CONFIG.FRONTEND_ACTIVATE_DOMAIN) {
        setTimeout(() => {
          EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
            type: 'error',
            alert: {
              message: t('label.pageNotFound')
            },
            callbacks: {
              eventOnClose: () => {
                next('/menu')
              }
            }
          })
        }, 1)

        return false
      }

      return true
    },
    smartText: () => {
      if (!CurrentUser.hasUserRightSmartText()) {
        next('/menu')
        return false
      }
      return true
    },
    showWarningDialog: () => {
      setTimeout(() => {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'error',
          alert: {
            message: t('label.noPermissionToAccessThePage')
          },
          callbacks: {
            eventOnClose: () => {
              next('/menu')
            }
          }
        })
      }, 1)
    }
  }

  if (pageTransitionContract && typeof (pageTransitionContract[transitionToPageName]) === 'function') {
    const returnValue = pageTransitionContract[transitionToPageName]()
    if (returnValue) {
      EventBus.$emit('CLOSE_FULL_SCREEN_VIDEO', { isVisible: false })
      next()
    }
  } else {
    EventBus.$emit('CLOSE_FULL_SCREEN_VIDEO', { isVisible: false })
    next()
  }
})

export default RouterConfig
